import { Circle, Polygon } from '@react-google-maps/api';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { extractLatLngFromPosition } from '../../../util/geoUtil';

enum DrawingMode {
  NONE = 'none',
  MARKER = 'marker',
  CIRCLE = 'circle',
  POLYGON = 'polygon',
}

interface DrawingControlsProps {
  currentMode: DrawingMode;
  onModeChange: (mode: DrawingMode) => void;
  disabled?: boolean;
  position?: 'horizontal' | 'vertical';
}

const DrawingControls: React.FC<DrawingControlsProps> = ({
  currentMode,
  onModeChange,
  disabled = false,
  position = 'horizontal',
}) => {
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: position === 'horizontal' ? 'row' : 'column',
    gap: '4px',
    padding: '8px',
    backgroundColor: 'white',
    borderRadius: '4px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
    margin: '10px',
  };

  const buttonStyle = (isActive: boolean): React.CSSProperties => ({
    padding: '8px 12px',
    border: 'none',
    borderRadius: '2px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    backgroundColor: isActive ? '#4285F4' : '#fff',
    color: isActive ? '#fff' : '#333',
    fontSize: '13px',
    fontFamily: 'Roboto, Arial, sans-serif',
    opacity: disabled ? 0.6 : 1,
    minWidth: '60px',
  });

  return (
    <div style={containerStyle}>
      <button
        type="button"
        style={buttonStyle(currentMode === DrawingMode.MARKER)}
        onClick={() =>
          onModeChange(currentMode === DrawingMode.MARKER ? DrawingMode.NONE : DrawingMode.MARKER)
        }
        disabled={disabled}
        title="Draw marker"
      >
        📍
      </button>
      <button
        type="button"
        style={buttonStyle(currentMode === DrawingMode.CIRCLE)}
        onClick={() =>
          onModeChange(currentMode === DrawingMode.CIRCLE ? DrawingMode.NONE : DrawingMode.CIRCLE)
        }
        disabled={disabled}
        title="Draw circle"
      >
        ⭕
      </button>
      <button
        type="button"
        style={buttonStyle(currentMode === DrawingMode.POLYGON)}
        onClick={() =>
          onModeChange(currentMode === DrawingMode.POLYGON ? DrawingMode.NONE : DrawingMode.POLYGON)
        }
        disabled={disabled}
        title="Draw polygon"
      >
        📐
      </button>
    </div>
  );
};

interface UseTTDrawingManagerProps {
  map?: google.maps.Map;
  onMarkerComplete?: (pos: google.maps.LatLngLiteral) => void;
  onCircleComplete?: (circle: { center: google.maps.LatLngLiteral; radius: number }) => void;
  onPolygonComplete?: (polygon: { points: google.maps.LatLngLiteral[] }) => void;
  isEditable?: boolean;
}

export const useTTDrawingManager = ({
  map,
  onMarkerComplete,
  onCircleComplete,
  onPolygonComplete,
  isEditable = false,
}: UseTTDrawingManagerProps) => {
  const [drawingMode, setDrawingMode] = useState<DrawingMode>(DrawingMode.NONE);
  const [isDrawing, setIsDrawing] = useState(false);
  const [polygonPath, setPolygonPath] = useState<google.maps.LatLngLiteral[]>([]);
  const [circleCenter, setCircleCenter] = useState<google.maps.LatLngLiteral | null>(null);
  const [currentRadius, setCurrentRadius] = useState(0);
  const [mousePosition, setMousePosition] = useState<google.maps.LatLngLiteral | null>(null);

  const drawingListeners = useRef<google.maps.MapsEventListener[]>([]);
  const circleCenterRef = useRef<google.maps.LatLngLiteral | null>(null);

  const cleanupListeners = useCallback(() => {
    drawingListeners.current.forEach(listener => listener.remove());
    drawingListeners.current = [];
  }, []);

  const handleMarkerDraw = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (!e.latLng || !onMarkerComplete) return;
      const position = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      onMarkerComplete(position);
      setDrawingMode(DrawingMode.NONE);
    },
    [onMarkerComplete],
  );

  const handleCircleDraw = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return;
      const position = { lat: e.latLng.lat(), lng: e.latLng.lng() };

      if (!isDrawing) {
        setCircleCenter(position);
        circleCenterRef.current = position;
        setIsDrawing(true);
        setCurrentRadius(0);
      } else if (onCircleComplete && circleCenterRef.current) {
        const center = circleCenterRef.current;
        const radius = google.maps.geometry.spherical.computeDistanceBetween(
          new google.maps.LatLng(center.lat, center.lng),
          new google.maps.LatLng(position.lat, position.lng),
        );
        onCircleComplete({ center, radius });
        setIsDrawing(false);
        setCircleCenter(null);
        circleCenterRef.current = null;
        setCurrentRadius(0);
        setDrawingMode(DrawingMode.NONE);
      }
    },
    [isDrawing, onCircleComplete],
  );

  const handlePolygonDraw = useCallback((e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;
    const position = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    setPolygonPath(prev => [...prev, position]);
    setIsDrawing(true);
  }, []);

  const handlePolygonComplete = useCallback(() => {
    if (polygonPath.length >= 3 && onPolygonComplete) {
      onPolygonComplete({ points: polygonPath });
    }
    setPolygonPath([]);
    setIsDrawing(false);
    setDrawingMode(DrawingMode.NONE);
  }, [polygonPath, onPolygonComplete]);

  useEffect(() => {
    if (!map || !isEditable) return undefined;

    cleanupListeners();

    if (drawingMode !== DrawingMode.NONE) {
      map.setOptions({ draggableCursor: 'crosshair' });

      const mouseMoveL = map.addListener('mousemove', (e: google.maps.MapMouseEvent) => {
        if (!e.latLng) return;
        const position = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        setMousePosition(position);

        if (drawingMode === DrawingMode.CIRCLE && isDrawing && circleCenterRef.current) {
          const center = circleCenterRef.current;
          const radius = google.maps.geometry.spherical.computeDistanceBetween(
            new google.maps.LatLng(center.lat, center.lng),
            new google.maps.LatLng(position.lat, position.lng),
          );
          setCurrentRadius(radius);
        }
      });

      const clickL = map.addListener('click', (e: google.maps.MapMouseEvent) => {
        switch (drawingMode) {
          case DrawingMode.MARKER:
            handleMarkerDraw(e);
            break;
          case DrawingMode.CIRCLE:
            handleCircleDraw(e);
            break;
          case DrawingMode.POLYGON:
            handlePolygonDraw(e);
            break;
          default:
            break;
        }
      });

      const rightClickL = map.addListener('rightclick', (e: google.maps.MapMouseEvent) => {
        if (drawingMode === DrawingMode.POLYGON) {
          e.stop();
          handlePolygonComplete();
        }
      });

      drawingListeners.current.push(clickL, rightClickL, mouseMoveL);
    } else {
      map.setOptions({ draggableCursor: undefined });
    }

    return cleanupListeners;
  }, [
    map,
    drawingMode,
    isEditable,
    handleMarkerDraw,
    handleCircleDraw,
    handlePolygonDraw,
    handlePolygonComplete,
    cleanupListeners,
    isDrawing,
  ]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cleanupListeners();
        setPolygonPath([]);
        setIsDrawing(false);
        setCircleCenter(null);
        circleCenterRef.current = null;
        setCurrentRadius(0);
        setDrawingMode(DrawingMode.NONE);
        setMousePosition(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [cleanupListeners]);

  const previewCircle = useMemo(
    () =>
      circleCenter && isDrawing ? (
        <Circle
          center={circleCenter}
          radius={currentRadius}
          options={{
            fillOpacity: 0.1,
            strokeWeight: 2,
            strokeOpacity: 0.8,
            strokeColor: '#4285F4',
            fillColor: '#4285F4',
            clickable: false,
            zIndex: 1000,
          }}
        />
      ) : null,
    [circleCenter, isDrawing, currentRadius],
  );

  const previewPolygon = useMemo(() => {
    if (polygonPath.length === 0) return null;

    const path =
      mousePosition && polygonPath.length > 0 ? [...polygonPath, mousePosition] : polygonPath;

    return (
      <Polygon
        path={path}
        options={{
          fillOpacity: 0.1,
          strokeWeight: 2,
          strokeOpacity: 0.8,
          strokeColor: '#4285F4',
          fillColor: '#4285F4',
          clickable: false,
          zIndex: 1000,
        }}
      />
    );
  }, [polygonPath, mousePosition]);

  return {
    drawingMode,
    setDrawingMode,
    isDrawing,
    polygonPath,
    circleCenter,
    currentRadius,
    previewCircle,
    previewPolygon,
  };
};

interface TTDrawingManagerProps {
  map?: google.maps.Map;
  onMarkerComplete?: (pos: google.maps.LatLngLiteral) => void;
  onCircleComplete?: (circle: { center: google.maps.LatLngLiteral; radius: number }) => void;
  onPolygonComplete?: (polygon: { points: google.maps.LatLngLiteral[] }) => void;
  isEditable?: boolean;
  showControls?: boolean;
  controlPosition?: google.maps.ControlPosition;
  controlOrientation?: 'horizontal' | 'vertical';
}

export const CustomDrawingManager: React.FC<TTDrawingManagerProps> = ({
  map,
  onMarkerComplete,
  onCircleComplete,
  onPolygonComplete,
  isEditable = false,
  showControls = true,
  controlPosition = google.maps.ControlPosition.TOP_RIGHT,
  controlOrientation = 'horizontal',
}) => {
  const { drawingMode, setDrawingMode, previewCircle, previewPolygon } = useTTDrawingManager({
    map,
    onMarkerComplete,
    onCircleComplete,
    onPolygonComplete,
    isEditable,
  });

  useEffect(() => {
    if (!map || !showControls || !isEditable) return undefined;
    const controlDiv = document.createElement('div');
    map.controls[controlPosition].push(controlDiv);
    const root = createRoot(controlDiv);
    root.render(
      <DrawingControls
        currentMode={drawingMode}
        onModeChange={setDrawingMode}
        disabled={!isEditable}
        position={controlOrientation}
      />,
    );
    return () => {
      root.unmount();
      const idx = map.controls[controlPosition].getArray().indexOf(controlDiv);
      if (idx > -1) map.controls[controlPosition].removeAt(idx);
    };
  }, [
    map,
    showControls,
    isEditable,
    controlPosition,
    controlOrientation,
    drawingMode,
    setDrawingMode,
  ]);

  return (
    <>
      {previewCircle}
      {previewPolygon}
    </>
  );
};

// Hook for using AdvancedMarkerElement with fallback to regular Marker
interface UseAdvancedMarkerProps {
  map?: google.maps.Map;
  position: google.maps.LatLngLiteral;
  content?: HTMLElement;
  draggable?: boolean;
  visible?: boolean;
  zIndex?: number;
  onDragEnd?: (position: google.maps.LatLngLiteral) => void;
}

export const useAdvancedMarker = ({
  map,
  position,
  content,
  draggable = false,
  visible = true,
  zIndex,
  onDragEnd,
}: UseAdvancedMarkerProps) => {
  const [marker, setMarker] = useState<{
    map: google.maps.Map | null | undefined;
    position: any;
    setMap: (map: google.maps.Map | null | undefined) => {};
    setPosition: (position: { lat: number; lng: number }) => {};
  } | null>(null);
  const dragEndRef = useRef<((position: { lat: number; lng: number }) => void) | undefined>(
    undefined,
  );
  dragEndRef.current = onDragEnd;

  // Create marker when dependencies change (excluding position)
  useEffect(() => {
    // If no map or not visible, ensure any existing marker is removed and skip creation
    if (!map || !visible) {
      if (marker) {
        try {
          if (marker.map !== undefined) {
            marker.map = null;
          } else if (marker.setMap) {
            marker.setMap(null);
          }
        } catch (err) {
          // no-op
        }
        setMarker(null);
      }
      return undefined;
    }

    // Clean up existing marker first
    if (marker) {
      if (marker.map) {
        marker.map = null;
      } else if (marker.setMap) {
        marker.setMap(null);
      }
      setMarker(null);
    }

    let newMarker: any;

    try {
      // Prefer marker namespace, fall back to global
      const g: any = (window as any).google;
      const AdvancedMarkerCtor: any =
        g?.maps?.marker?.AdvancedMarkerElement || g?.maps?.AdvancedMarkerElement || null;

      if (AdvancedMarkerCtor) {
        // Use AdvancedMarkerElement if available
        // Log Map ID for debugging the initial render issue
        if (process.env.NODE_ENV !== 'production') {
          console.log(
            '[useAdvancedMarker] Creating AdvancedMarkerElement, mapId:',
            (map as any)?.__gm?.mapId,
          );
        }

        newMarker = new AdvancedMarkerCtor({
          map: visible ? map : null,
          position,
          content,
          gmpDraggable: draggable,
          zIndex,
        });

        // Note: no marker-level click event interception here; rely on map options to control click behavior

        if (draggable) {
          newMarker.addListener('dragend', () => {
            const pos = newMarker.position;
            if (!pos) return;
            const positionLatLng = extractLatLngFromPosition(pos);
            dragEndRef.current?.({ lat: positionLatLng.lat, lng: positionLatLng.lng });
          });
        }
      } else {
        // Fallback to regular Marker

        // Create icon from content if provided
        let icon: google.maps.Icon | undefined;
        if (content) {
          // Extract inner <svg> if present; otherwise, support <img> src; otherwise use 1x1 transparent pixel
          const svgEl =
            (content as any).nodeName === 'svg'
              ? (content as unknown as SVGElement)
              : content.querySelector && content.querySelector('svg');
          const imgEl =
            !svgEl && content.querySelector
              ? (content.querySelector('img') as HTMLImageElement | null)
              : null;

          if (svgEl) {
            const svgString = (svgEl as SVGElement).outerHTML;
            const dataUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgString)}`;
            icon = {
              url: dataUrl,
              scaledSize: new google.maps.Size(40, 40),
            } as google.maps.Icon;
          } else if (imgEl && imgEl.src) {
            icon = {
              url: imgEl.src,
              scaledSize: new google.maps.Size(imgEl.width || 40, imgEl.height || 40),
            } as google.maps.Icon;
          } else {
            icon = {
              url: 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=',
              scaledSize: new google.maps.Size(1, 1),
            } as google.maps.Icon;
          }
        } else {
          icon = {
            url: 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=',
            scaledSize: new google.maps.Size(1, 1),
          } as google.maps.Icon;
        }

        newMarker = new google.maps.Marker({
          map: visible ? map : null,
          position,
          draggable,
          zIndex,
          icon,
          optimized: false, // Disable marker clustering for custom icons
        });

        // No legacy marker click interception

        if (draggable) {
          newMarker.addListener('dragend', () => {
            const pos = newMarker.getPosition();
            if (!pos) return;
            dragEndRef.current?.({ lat: pos.lat(), lng: pos.lng() });
          });
        }
      }

      setMarker(newMarker);
    } catch (error) {
      // Report marker creation errors only in development to avoid noisy production logs
      if (process.env.NODE_ENV !== 'production') {
        console.error('useAdvancedMarker - Error creating marker', error);
      }
    }

    return () => {
      if (newMarker?.map) {
        newMarker.map = null;
      }
      if (newMarker?.setMap) {
        newMarker.setMap(null);
      }
      setMarker(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, draggable, content, zIndex, visible]); // Removed position from dependencies

  // Update position when it changes (separate effect to avoid recreation)
  useEffect(() => {
    if (!marker || !position) return;
    const current = extractLatLngFromPosition(marker.position);
    if (current.lat === position.lat && current.lng === position.lng) return;

    const isALegacyMarker = marker.position === undefined && marker.setPosition;
    if (isALegacyMarker) {
      marker.setPosition(current);
    }
  }, [marker, position]);

  // Update visibility when it changes
  useEffect(() => {
    if (!marker) return;
    const mapRef: google.maps.Map | null | undefined = visible ? map : null;
    const mapAlreadySet = (marker.map !== undefined ? marker.map : null) === mapRef;
    if (mapAlreadySet) return;
    if (marker.map !== undefined) {
      marker.map = mapRef;
    } else if (marker.setMap) {
      marker.setMap(mapRef);
    }
  }, [marker, map, visible]);

  return marker;
};
